/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import Grid from "@mui/material/Grid";
import summaryStyle from "@/src/components/Form/Common/Style/summaryStyle";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { Box } from "@mui/material";
import { eur } from "@/src/services/formatService";
import { Item } from "@/src/components/Form/FamilyLaw/Alimony/AlimonyExpenses/AlimonyExpenses";
import { fromFloat } from "@/src/services/formServiceFunctions";

type PersonDataProps = {
  formData: AbstractCase | Record<string, any>;
  personType: string;
  title: string;
  translate: Function;
};

const PersonData = ({ formData, personType, title, translate }: PersonDataProps) => {
  const personData = formData[personType];

  return (
    <>
      <Grid sx={summaryStyle.subHeadline} size={12}>
        <Typography variant={"h6"}>{title}</Typography>
      </Grid>
      {personType === "alimonyPartner" && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".person.gender"} sx={summaryStyle.scrollToAnchor} />
            Anrede
          </Grid>
          <Grid size={12}>
            {personData.person.gender === "male" ? "Herr" : "Frau"}
          </Grid>

          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".person.givenName"} sx={summaryStyle.scrollToAnchor} />
            Vorname
          </Grid>
          <Grid size={12}>
            {personData.person.givenName}
          </Grid>

          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".person.familyName"} sx={summaryStyle.scrollToAnchor} />
            Nachname
          </Grid>
          <Grid size={12}>
            {personData.person.familyName}
          </Grid>
        </>
      )}
      <Grid sx={summaryStyle.question} size={12}>
        <Box id={personType + ".employmentStatus"} sx={summaryStyle.scrollToAnchor} />
        Beschäftigungsstatus
      </Grid>
      <Grid size={12}>
        {translate(`${personType}.employmentStatus.${personData.employmentStatus}`, formData)}
      </Grid>
      {personData.employmentStatus !== "unemployed" && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".netIncome"} sx={summaryStyle.scrollToAnchor} />
            Nettoeinkommen
          </Grid>
          <Grid size={12}>
            {eur(personData.netIncome)}
          </Grid>
        </>
      )}
      {personData.otherIncome > 0 && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".otherIncome"} sx={summaryStyle.scrollToAnchor} />
            Sonstiges Nettoeinnahmen
          </Grid>
          <Grid size={12}>
            {eur(personData.otherIncome)}
          </Grid>
        </>
      )}
      {formData.basicChoice === "complete" && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".hasMarriageRelatedDisadvantages"} sx={summaryStyle.scrollToAnchor} />
            Liegen ehebedingte Nachteile vor?
          </Grid>
          <Grid size={12}>
            {personData.hasMarriageRelatedDisadvantages === "yes" ? "Ja" : "Nein"}
          </Grid>
        </>
      )}
      <Grid sx={summaryStyle.question} size={12}>
        <Box id={personType + ".livingSituation"} sx={summaryStyle.scrollToAnchor} />
        Wohnsituation
      </Grid>
      <Grid size={12}>
        {translate(`${personType}.livingSituation.${personData.livingSituation}`, formData)}
      </Grid>
      {personData.livingExpenses !== null && personData.livingSituation === "tenant" && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".livingExpenses"} sx={summaryStyle.scrollToAnchor} />
            Monatliche Warmmiete
          </Grid>
          <Grid size={12}>
            {eur(personData.livingExpenses)}
          </Grid>
        </>
      )}
      {personData.monthlyMortgagePayment !== null && personData.livingSituation === "ownerWithMortgage" && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".monthlyImputedRent"} sx={summaryStyle.scrollToAnchor} />
            Monatliche Kreditrate
          </Grid>
          <Grid size={12}>
            {eur(personData.monthlyMortgagePayment)}
          </Grid>
        </>
      )}
      {personData.monthlyImputedRent !== null && ["ownerWithMortgage", "ownerOutright", "other"].includes(personData.livingSituation) && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".monthlyImputedRent"} sx={summaryStyle.scrollToAnchor} />
            Wohnwert
          </Grid>
          <Grid size={12}>
            {eur(personData.monthlyImputedRent)}
          </Grid>
        </>
      )}
      {personData.otherExpenses !== null && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".otherExpenses"} sx={summaryStyle.scrollToAnchor} />
            Sonstige Ausgaben
          </Grid>
          <Grid size={12}>
            {eur(personData.otherExpenses)}
          </Grid>
        </>
      )}
      {personData.expensesList.length > 0 && (
        <>
          <Grid sx={summaryStyle.question} size={12}>
            <Box id={personType + ".expensesList"} sx={summaryStyle.scrollToAnchor} />
            Übersicht der Ausgaben
          </Grid>
          {personData.expensesList.map((item: Item) => (
            <Grid size={12}>
              {translate(`expensesCategory.${item.category}`, formData)} - {item.name}: {eur(fromFloat(item.monthlyAmount))}
            </Grid>
          ))}
        </>
      )}
    </>
  );
};

export default PersonData;
